import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useApi } from "@/hooks/useApi";
import { useAppData } from "@/context/AppDataContext";
import { applicationService } from "@/services/ApplicationService";

export type QueueActionMode = "allocate" | "dead";

export interface AllocateActionPayload {
  departmentId: string | null;
  agentId: number;
  agentName: string;
}

export interface DeadActionPayload {
  statusTypeId: number;
  statusType: string;
  statusReasonId: number;
  statusReason: string;
  note: string;
  isImportant: boolean;
}

/** Mirrors the workscreens MoveApplicationDialog — "Dead" is status type 46. */
const DEAD_STATUS_ID = 46;

/**
 * Status types offered by the workscreens MoveApplicationDialog. Kept here so the
 * status dropdown can be unlocked later; for now it is forced to Dead.
 */
const ALLOWED_STATUS_TYPE_IDS = [44, 46, 48, 49, 53, 78, 88];

const MODE_OPTIONS: { value: QueueActionMode; label: string }[] = [
  { value: "allocate", label: "Allocate to agent" },
  { value: "dead", label: "Update application status to dead" },
];

interface QueueActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Reference shown in the dialog title. */
  applicationId: string | number;
  onAllocate: (payload: AllocateActionPayload) => Promise<void>;
  onMarkDead: (payload: DeadActionPayload) => Promise<void>;
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold text-muted-foreground">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {children}
    </div>
  );
}

export function QueueActionDialog({
  open,
  onOpenChange,
  applicationId,
  onAllocate,
  onMarkDead,
}: QueueActionDialogProps) {
  const { workers, workerOptions, departmentOptions } = useAppData();

  const [step, setStep] = useState<1 | 2>(1);
  const [mode, setMode] = useState<QueueActionMode | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Allocate step
  const [departmentId, setDepartmentId] = useState<string>("");
  const [agentId, setAgentId] = useState<number | null>(null);

  // Dead step
  const [statusTypeId, setStatusTypeId] = useState<number>(DEAD_STATUS_ID);
  const [statusReasonId, setStatusReasonId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [isImportant, setIsImportant] = useState(false);

  const onDeadStep = open && step === 2 && mode === "dead";

  const statusTypesResult = useApi(
    (signal) => applicationService.getLoanStatusTypes(signal),
    [],
    { skip: !onDeadStep }
  );
  const statusReasonsResult = useApi(
    (signal) => applicationService.getLoanStatusReasons(signal),
    [],
    { skip: !onDeadStep }
  );

  const statusTypeOptions = useMemo(
    () =>
      (statusTypesResult.data ?? []).filter(
        (s) => s.allowed && ALLOWED_STATUS_TYPE_IDS.includes(s.statusTypeId)
      ),
    [statusTypesResult.data]
  );

  const reasonOptions = useMemo(
    () =>
      (statusReasonsResult.data ?? []).filter(
        (r) => r.isActive && r.isActiveForUser && r.statusTypeId === statusTypeId
      ),
    [statusReasonsResult.data, statusTypeId]
  );

  const dealerIntroducedDepartmentOptions = useMemo(
    () => departmentOptions.filter((d) => d.label.toLowerCase().includes("dealer introduced")),
    [departmentOptions]
  );

  // Agents are narrowed to the chosen team, matching the Pipeline filters.
  const agentOptions = useMemo(() => {
    if (!departmentId) return workerOptions;
    return workers
      .filter((w) => w.departmentId === departmentId && w?.userId != null && w?.name != null)
      .map((w) => ({ value: w.userId, label: w.name }));
  }, [workers, workerOptions, departmentId]);

  const reset = () => {
    setStep(1);
    setMode("");
    setError(null);
    setDepartmentId("");
    setAgentId(null);
    setStatusTypeId(DEAD_STATUS_ID);
    setStatusReasonId(null);
    setNote("");
    setIsImportant(false);
  };

  useEffect(() => {
    if (open) reset();
  }, [open]);

  const canComplete =
    mode === "allocate" ? agentId != null : mode === "dead" ? statusReasonId != null : false;

  const handleComplete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (mode === "allocate" && agentId != null) {
        await onAllocate({
          departmentId: departmentId || null,
          agentId,
          agentName: agentOptions.find((a) => a.value === agentId)?.label ?? "",
        });
      } else if (mode === "dead" && statusReasonId != null) {
        await onMarkDead({
          statusTypeId,
          statusType:
            statusTypeOptions.find((s) => s.statusTypeId === statusTypeId)?.statusType ?? "Dead",
          statusReasonId,
          statusReason:
            reasonOptions.find((r) => r.statusReasonId === statusReasonId)?.statusReason ?? "",
          note: note.trim(),
          isImportant,
        });
      }
      onOpenChange(false);
    } catch {
      setError("Failed to complete the action. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Action Request — {applicationId}</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="flex flex-col gap-2 py-2">
            {MODE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors",
                  mode === option.value
                    ? "border-purple-600 bg-purple-50"
                    : "border-border hover:bg-muted/50"
                )}
              >
                <input
                  type="radio"
                  name="uw-queue-action"
                  className="size-4 accent-purple-600"
                  value={option.value}
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                />
                <span className="text-foreground">{option.label}</span>
              </label>
            ))}
          </div>
        )}

        {step === 2 && mode === "allocate" && (
          <div className="flex flex-col gap-4 py-2">
            <Field label="Team">
              <Select
                value={departmentId}
                onValueChange={(v) => {
                  setDepartmentId(v);
                  setAgentId(null);
                }}
                disabled={submitting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent position="popper">
                  {dealerIntroducedDepartmentOptions.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Agent" required>
              <Select
                value={agentId != null ? String(agentId) : ""}
                onValueChange={(v) => setAgentId(Number(v))}
                disabled={submitting}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent position="popper">
                  {agentOptions.map((a) => (
                    <SelectItem key={a.value} value={String(a.value)}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}

        {onDeadStep && (
          <div className="flex flex-col gap-4 py-2">
            <Field label="Status">
              {/* Locked to Dead for now — the dropdown stays so it can be opened up later. */}
              <Select value={String(statusTypeId)} onValueChange={(v) => setStatusTypeId(Number(v))} disabled>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Dead" />
                </SelectTrigger>
                <SelectContent position="popper">
                  {statusTypeOptions.map((s) => (
                    <SelectItem key={s.statusTypeId} value={String(s.statusTypeId)}>
                      {s.statusType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Reason" required>
              <Select
                value={statusReasonId != null ? String(statusReasonId) : ""}
                onValueChange={(v) => setStatusReasonId(Number(v))}
                disabled={submitting || statusReasonsResult.loading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={statusReasonsResult.loading ? "Loading reasons…" : "Select a reason"}
                  />
                </SelectTrigger>
                <SelectContent position="popper">
                  {reasonOptions.map((r) => (
                    <SelectItem key={r.statusReasonId} value={String(r.statusReasonId)}>
                      {r.statusReason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Note">
              <textarea
                rows={5}
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-slate-600"
                placeholder="Add a note…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={submitting}
              />
            </Field>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={isImportant}
                onCheckedChange={(v) => setIsImportant(v === true)}
                disabled={submitting}
              />
              Flag this note as important
            </label>

            {statusReasonsResult.error && (
              <p className="text-sm text-rose-600">Could not load reasons: {statusReasonsResult.error}</p>
            )}
          </div>
        )}

        {error && <p className="text-sm font-medium text-rose-600">{error}</p>}

        <DialogFooter className="gap-3 sm:gap-3">
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {step === 2 && (
            <Button variant="outline" disabled={submitting} onClick={() => setStep(1)}>
              Back
            </Button>
          )}
          {step === 1 ? (
            <Button
              className="bg-purple-600 text-white hover:bg-purple-700"
              disabled={!mode}
              onClick={() => setStep(2)}
            >
              Next
            </Button>
          ) : (
            <Button
              className="bg-purple-600 text-white hover:bg-purple-700"
              disabled={submitting || !canComplete}
              onClick={handleComplete}
            >
              {submitting && <Loader2 className="mr-1 size-3.5 animate-spin" />}
              Complete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
